// Monitoring Service API — негізгі бэкенд арқылы (прокси).
// Мониторингтің /admin/* endpoint-тері енді құпия кілтпен жабық; бэкенд Google
// токені мен рөлді тексеріп, кілтті өзі қосады.
import { API_URL } from "../config/connect";

const MONITORING_API_URL = `${API_URL}/api/v1/admin/monitoring`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('google_id_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Get admin statistics
 * GET /admin/stats
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date in ISO format (e.g., "2024-01-01T00:00:00Z")
 * @param {string} params.end_date - End date in ISO format (e.g., "2024-01-31T23:59:59Z")
 * @returns {Promise<Object>} Statistics data including:
 *   - Total events count
 *   - Unique users and devices
 *   - Breakdown by event type
 *   - Daily statistics (last 30 days)
 *   - Most visited pages
 *   - Most searched queries
 *   - Success/failure rates (login, registration, advertise)
 */
export const getAdminStats = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const url = `${MONITORING_API_URL}/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching admin stats:', error);
    throw error;
  }
};

/**
 * Get events list with filters and pagination
 * GET /admin/events
 * @param {Object} params - Query parameters
 * @param {string} params.event_type - Filter by event type
 * @param {string} params.user_id - Filter by user ID
 * @param {string} params.device_id - Filter by device ID
 * @param {string} params.start_date - Start date (ISO format)
 * @param {string} params.end_date - End date (ISO format)
 * @param {number} params.limit - Items per page (default: 50)
 * @param {number} params.offset - Offset for pagination (default: 0)
 * @param {string} params.order_by - Field to sort by (default: 'timestamp')
 * @param {string} params.order_dir - Sort direction: 'asc' or 'desc' (default: 'desc')
 * @returns {Promise<Object>} Events list with pagination
 */
export const getAdminEvents = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.event_type) queryParams.append('event_type', params.event_type);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.device_id) queryParams.append('device_id', params.device_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.order_by) queryParams.append('order_by', params.order_by);
    if (params.order_dir) queryParams.append('order_dir', params.order_dir);
    
    const url = `${MONITORING_API_URL}/events${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching admin events:', error);
    throw error;
  }
};

/**
 * Get user analytics
 * GET /admin/users/{user_id}/analytics
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User analytics data including:
 *   - All user events
 *   - Breakdown by event type
 *   - First and last activity
 *   - Most visited pages
 */
export const getUserAnalytics = async (userId) => {
  try {
    const response = await fetch(`${MONITORING_API_URL}/users/${userId}/analytics`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user analytics:', error);
    throw error;
  }
};



const getJSON = async (path, params = {}) => {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));
  const response = await fetch(`${MONITORING_API_URL}/${path}${q.toString() ? `?${q}` : ""}`, { headers: getAuthHeaders() });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(`${response.status}: ${errorData.detail || errorData.message || response.statusText}`);
  }
  return response.json();
};

/** Сұраулар: жиынтық, уақыт қатары, endpoint-тер (p50/p95), ең баяулары, платформалар. */
export const getRequestMetrics = (params = {}) => getJSON("requests", params);

/** Тірі ағын: соңғы оқиғалар (after_id — тек жаңаларын алу үшін). */
export const getLiveFeed = (params = {}) => getJSON("live", params);
